import { useState } from 'react';
import { Activity, RefreshCw, ScrollText, ShieldCheck } from 'lucide-react';
import { PageHead } from '../components/bits.jsx';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHead,
  EmptyState,
  Loading,
  SearchInput,
  Segmented,
  Stat,
} from '../components/ui.jsx';
import { useApi, useDebounced } from '../lib/hooks.js';
import { dateTimeLabel, num, relative, titleCase } from '../lib/format.js';

const ACTOR_TYPES = [
  { value: 'all', label: 'Everyone' },
  { value: 'user', label: 'Office staff' },
  { value: 'lumper', label: 'Lumpers' },
  { value: 'system', label: 'System' },
];

export default function AuditLog() {
  const [actorType, setActorType] = useState('all');
  const [entityType, setEntityType] = useState('all');
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 300);

  const { data, meta, loading, refresh } = useApi('/audit', {
    params: { actorType, entityType, q: debounced || undefined, limit: 200 },
  });

  const entries = data || [];
  const facets = meta?.facets || {};

  return (
    <>
      <PageHead
        title="History of changes"
        subtitle="Every change anyone made, kept forever in case we need to look back."
        icon={ScrollText}
        actions={<Button variant="outline" size="sm" icon={RefreshCw} onClick={refresh}>Refresh</Button>}
      />

      <div className="grid grid-4 mb-20">
        <Stat label="Everything recorded" icon={ScrollText} value={num(facets.total)} tone="amber" foot="retained forever" />
        <Stat label="Today" icon={Activity} value={num(facets.today)} tone="blue" foot="actions recorded" />
        <Stat label="Kinds of change" icon={ShieldCheck} value={num(facets.actions?.length)} tone="violet" foot="event types" />
        <Stat label="Kinds of record" icon={ScrollText} value={num(facets.entities?.length)} tone="teal" foot="records touched" />
      </div>

      <div className="split-wide">
        <Card>
          <div className="filter-bar" style={{ border: 0, borderRadius: 0, margin: 0, background: 'transparent' }}>
            <SearchInput value={query} onChange={setQuery} placeholder="Search for a change…" style={{ minWidth: 220, maxWidth: 300 }} />
            <Segmented options={ACTOR_TYPES} value={actorType} onChange={setActorType} />
            <select className="select select-sm" value={entityType} onChange={(e) => setEntityType(e.target.value)} style={{ maxWidth: 170 }}>
              <option value="all">All records</option>
              {(facets.entities || []).map((entity) => (
                <option key={entity.entityType} value={entity.entityType}>
                  {titleCase(entity.entityType)} ({entity.n})
                </option>
              ))}
            </select>
            <div className="grow" />
            <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{entries.length} shown</span>
          </div>
          {loading && !entries.length ? (
            <Loading rows={8} />
          ) : entries.length ? (
            <div className="table-wrap" style={{ maxHeight: 620 }}>
              <table className="table">
                <thead>
                  <tr>
                    <th className="narrow">When</th>
                    <th>What happened</th>
                    <th>Who did it</th>
                    <th>Details</th>
                    <th className="narrow">Which record</th>
                    <th className="narrow">Where from</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry.id}>
                      <td className="narrow">
                        <div className="col gap-2">
                          <span className="num" style={{ fontSize: 'var(--fs-xs)' }}>{dateTimeLabel(entry.at)}</span>
                          <span className="dimmer" style={{ fontSize: 10 }}>{relative(entry.at)}</span>
                        </div>
                      </td>
                      <td>
                        <Badge
                          size="sm"
                          tone={entry.action.includes('delete') || entry.action.includes('suspend') || entry.action.includes('cancel') ? 'red' : entry.action.includes('paid') || entry.action.includes('approved') ? 'green' : 'muted'}
                        >
                          {entry.action}
                        </Badge>
                      </td>
                      <td>
                        <div className="row gap-8">
                          <Avatar name={entry.actorName} tone={entry.actorType === 'lumper' ? 'blue' : entry.actorType === 'system' ? 'muted' : 'amber'} size="sm" />
                          <span className="truncate" style={{ fontSize: 'var(--fs-sm)' }}>{entry.actorName}</span>
                        </div>
                      </td>
                      <td className="truncate dim">{entry.summary}</td>
                      <td className="narrow">
                        <span className="mono dim" style={{ fontSize: 'var(--fs-xs)' }}>
                          {entry.entityType}{entry.entityId ? `#${entry.entityId}` : ''}
                        </span>
                      </td>
                      <td className="narrow dim mono" style={{ fontSize: 'var(--fs-xs)' }}>{entry.ip || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState icon={ScrollText} title="No audit entries match this view" />
          )}
        </Card>

        <div className="col gap-20">
          <Card>
            <CardHead title="Most common changes" icon={Activity} />
            <CardBody className="col gap-8">
              {(facets.actions || []).slice(0, 12).map((action) => (
                <div key={action.action} className="row between gap-8">
                  <span className="truncate mono" style={{ fontSize: 'var(--fs-xs)' }}>{action.action}</span>
                  <Badge tone="muted" size="sm">{num(action.n)}</Badge>
                </div>
              ))}
              {!facets.actions?.length ? <span className="dim">Nothing recorded yet.</span> : null}
            </CardBody>
          </Card>

          <Card>
            <CardHead title="Who makes the most changes" icon={ShieldCheck} />
            <CardBody className="col gap-10">
              {(facets.actors || []).slice(0, 8).map((actor) => (
                <div key={actor.actorName} className="row between gap-8">
                  <span className="row gap-8 truncate">
                    <Avatar name={actor.actorName} tone="amber" size="sm" />
                    <span className="truncate" style={{ fontSize: 'var(--fs-sm)' }}>{actor.actorName}</span>
                  </span>
                  <span className="num dim" style={{ fontSize: 'var(--fs-sm)' }}>{num(actor.n)}</span>
                </div>
              ))}
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
}
